import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Search,
  FileText,
  Users,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  User,
  //BarChart3, Use for Reports and Analytics
  Upload,
  Gavel,
  //FileCheck,
  Star,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Search', href: '/search', icon: Search },
    { name: 'Documents', href: '/documents', icon: FileText },
    { name: 'Favorites', href: '/favorites', icon: Star },
    ...(user?.role === 'document_admin'
      ? [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Upload', href: '/documents/upload', icon: Upload },
        { name: 'Document Types', href: '/admin/document-types', icon: FileText },
        { name: 'Courts', href: '/admin/courts', icon: Gavel },
      ]
      : []),
    ...(user?.role === 'super_admin'
      ? [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Upload', href: '/documents/upload', icon: Upload },
        { name: 'Document Types', href: '/admin/document-types', icon: FileText },
        { name: 'Courts', href: '/admin/courts', icon: Gavel },
        { name: 'Users', href: '/admin/users', icon: Users },

        // { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
        // { name: 'Reports', href: '/admin/reports', icon: FileCheck },
      ]
      : []),
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:inset-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Gavel className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <span className="ml-2 text-xl font-semibold text-gray-900 dark:text-white">
              MNA Associates
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-5 px-2 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-item ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-400" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                {user?.first_name} {user?.last_name}
              </span>
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="ml-4 text-lg font-medium text-gray-900 dark:text-white">
                {navigation.find(item => isActive(item.href))?.name || 'Dashboard'}
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {user?.role === 'super_admin' && 'Super Admin'}
                  {user?.role === 'document_admin' && 'Document Admin'}
                  {user?.role === 'regular_user' && 'User'}
                </span>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>

              <div className="relative">
                <button
                  onClick={() => navigate('/profile')}
                  className="flex items-center space-x-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden sm:block text-sm text-gray-700 dark:text-gray-300">
                    Profile
                  </span>
                </button>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 p-2 rounded-md text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <LogOut className="h-5 w-5" />
                <span className="hidden sm:block text-sm">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
